import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  getQueryHistoryUsers,
  getQueryHistoryStats,
  getQueryHistory,
  deleteQueryHistoryEntry,
  getTodayQueries,
  getReportRecipients,
  setAdminReportNotification,
  setPmReportNotification,
  sendReportNow,
} from "../api/guru";
import AccountMenu from "../components/AccountMenu";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import guruLogo from "../assets/guru-logo.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChartRenderer from "../components/ChartRenderer";

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLE_LABELS = {
  admin:           { label: "Admin",           emoji: "🔑" },
  project_manager: { label: "Project Manager", emoji: "📋" },
  employee:        { label: "Employee",        emoji: "👤" },
};

const CONNECTOR_LABELS = {
  gsc:               "Google Search Console",
  ga4:               "Google Analytics",
  knowledge_base:    "Knowledge Base",
  web_search:        "Web Search",
  karma:             "Karma",
  karma1:            "Karma (Primary)",
  karma2:            "Karma (Secondary)",
  sheets:            "Google Sheets",
  gbp:               "Google Business Profile",
  llm_knowledge:     "LLM Knowledge",
  seo_best_practice: "SEO Best Practice",
  project_team:      "Project Registry",
  guard:             "Off-topic (Blocked)",
  error:             "Error",
  kb:                "Knowledge Base",
  web:               "Web Search",
};

const SOURCE_PICKER_LABELS = {
  gsc:          "GSC",
  ga4:          "GA4",
  gsheet:       "Google Sheets",
  karma1:       "Karma 1",
  karma2:       "Karma 2",
  gbp:          "GMB",
  project_team: "Project Team",
  kb:           "Knowledge Base",
  web:          "Web",
};

function selectedSourcesLabel(raw) {
  if (!raw || raw === "all" || raw.trim() === "") return "All connectors";
  return raw.split(",").map(s => SOURCE_PICKER_LABELS[s.trim()] || s.trim()).join(", ");
}

function connectorLabel(raw) {
  if (!raw) return "—";
  if (raw.includes(",")) return "Multi Connector";
  return CONNECTOR_LABELS[raw] || raw;
}

const fmt = {
  datetime: (iso) => iso ? new Date(iso).toLocaleString() : "—",
  time:     (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
};

// ── Shared helpers ────────────────────────────────────────────────────────────

function AnswerContent({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      className="message-md"
      components={{
        code({ inline, className, children }) {
          const match = /language-(\w+)/.exec(className || "");
          if (!inline && match && match[1] === "chart") {
            try {
              const spec = JSON.parse(String(children).trim());
              return <ChartRenderer spec={spec} />;
            } catch (e) {
              return <pre className="chart-error">Chart parse error: {e.message}</pre>;
            }
          }
          return <code className={className}>{children}</code>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  return (
    <div className={"alert alert-" + type} style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit" }}>✕</button>
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", cursor: disabled ? "not-allowed" : "pointer" }}>
      <span style={{ position: "relative", display: "inline-block", width: 40, height: 22 }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => !disabled && onChange(e.target.checked)}
          disabled={disabled}
          style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
        />
        <span style={{
          position: "absolute", inset: 0,
          background: checked ? "#FF6B1A" : "#d1d5db",
          borderRadius: 11, transition: "background 0.2s",
        }} />
        <span style={{
          position: "absolute", top: 3, left: checked ? 21 : 3,
          width: 16, height: 16, background: "#fff", borderRadius: "50%",
          transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </span>
    </label>
  );
}

function ConnectorSelect({ value, onChange, style }) {
  return (
    <select className="admin-inline-input" value={value} onChange={e => onChange(e.target.value)} style={{ fontSize: 13, padding: "5px 10px", ...style }}>
      <option value="">All connectors</option>
      <option value="gsc">Google Search Console</option>
      <option value="ga4">Google Analytics</option>
      <option value="knowledge_base">Knowledge Base</option>
      <option value="web_search">Web Search</option>
      <option value="karma">Karma</option>
      <option value="sheets">Google Sheets</option>
      <option value="gbp">Google Business Profile</option>
      <option value="llm_knowledge">LLM Knowledge</option>
      <option value="guard">Off-topic (Blocked)</option>
      <option value="error">Error</option>
    </select>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Members + per-user history
// ═══════════════════════════════════════════════════════════════════════════════

const ROLE_FILTERS = [
  { key: "all",             label: "All" },
  { key: "admin",           label: "🔑 Admin" },
  { key: "project_manager", label: "📋 Project Manager" },
  { key: "employee",        label: "👤 Employee" },
];

const QH_DEPT_COLORS = {
  "SEO":                    { bg: "#eff6ff", color: "#1d4ed8" },
  "Social Media":           { bg: "#fdf4ff", color: "#7e22ce" },
  "Performance Marketing":  { bg: "#fff7ed", color: "#c2410c" },
  "Content":                { bg: "#f0fdf4", color: "#15803d" },
  "Development":            { bg: "#f0f9ff", color: "#0369a1" },
  "Design":                 { bg: "#fdf2f8", color: "#9d174d" },
  "Sales":                  { bg: "#fefce8", color: "#92400e" },
  "HR":                     { bg: "#f9fafb", color: "#374151" },
  "Accounts":               { bg: "#f0fdf4", color: "#166534" },
  "Operations":             { bg: "#fff1f2", color: "#be123c" },
  "Management":             { bg: "#fefce8", color: "#713f12" },
};

function QHDeptBadge({ dept }) {
  if (!dept) return null;
  const c = QH_DEPT_COLORS[dept] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: c.bg, color: c.color, marginLeft: 6 }}>
      {dept}
    </span>
  );
}

function PeopleList({ users, onSelectUser }) {
  const [search,     setSearch]     = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");

  const allUsers = [
    ...(users.admin           || []).map(u => ({ ...u, user_role: "admin" })),
    ...(users.project_manager || []).map(u => ({ ...u, user_role: "project_manager" })),
    ...(users.employee        || []).map(u => ({ ...u, user_role: "employee" })),
  ];

  const allDepts = ["all", ...Array.from(new Set(allUsers.map(u => u.department).filter(Boolean))).sort()];

  const filtered = allUsers.filter((u) => {
    const matchRole   = roleFilter === "all" || u.user_role === roleFilter;
    const matchDept   = deptFilter === "all" || u.department === deptFilter;
    const matchSearch = !search ||
      u.user_name.toLowerCase().includes(search.toLowerCase()) ||
      u.user_email.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchDept && matchSearch;
  });

  const totalByRole = {
    admin:           (users.admin           || []).length,
    project_manager: (users.project_manager || []).length,
    employee:        (users.employee        || []).length,
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Team Members</h2>
        <p className="panel-sub">Select a member to view their query activity.</p>
      </div>
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="admin-inline-input"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "100%", maxWidth: 420 }}
        />
      </div>
      <div className="admin-tabs" style={{ marginBottom: 10 }}>
        {ROLE_FILTERS.map((f) => {
          const count = f.key === "all" ? allUsers.length : (totalByRole[f.key] || 0);
          return (
            <button
              key={f.key}
              className={"admin-tab" + (roleFilter === f.key ? " active" : "")}
              onClick={() => setRoleFilter(f.key)}
            >
              {f.label}
              <span style={{
                marginLeft: 6, fontSize: 11, fontWeight: 600,
                background: roleFilter === f.key ? "rgba(255,107,26,0.15)" : "#f3f4f6",
                color: roleFilter === f.key ? "#FF6B1A" : "#6b7280",
                padding: "1px 7px", borderRadius: 10,
              }}>{count}</span>
            </button>
          );
        })}
      </div>
      {allDepts.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <select
            className="admin-inline-input"
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
            style={{ fontSize: 13, padding: "5px 10px" }}
          >
            <option value="all">All Departments</option>
            {allDepts.filter(d => d !== "all").map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      )}
      {filtered.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 14, padding: "12px 0" }}>No members found.</div>
      ) : (
        <div className="qh-person-list">
          {filtered.map((u) => {
            const meta = ROLE_LABELS[u.user_role] || { label: u.user_role, emoji: "👤" };
            return (
              <button key={u.user_email} className="qh-person-row" onClick={() => onSelectUser(u)}>
                <span className="qh-person-avatar">{(u.user_name || u.user_email)[0].toUpperCase()}</span>
                <span className="qh-person-info">
                  <span className="qh-person-name">{u.user_name}</span>
                  <span className="qh-person-email">{u.user_email}</span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span className="qh-role-pill">{meta.emoji} {meta.label}</span>
                  <QHDeptBadge dept={u.department} />
                </span>
                <span className="qh-person-arrow">→</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserHistoryView({ user: selectedUser, onBack, showAlert }) {
  const [stats,        setStats]        = useState(null);
  const [history,      setHistory]      = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [connFilter,   setConnFilter]   = useState("");
  const [dateFrom,     setDateFrom]     = useState("");
  const [dateTo,       setDateTo]       = useState("");
  const [expanded,     setExpanded]     = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingHist,  setLoadingHist]  = useState(true);
  const LIMIT = 50;

  const loadStats = useCallback(() => {
    setLoadingStats(true);
    getQueryHistoryStats(selectedUser.user_email)
      .then((r) => setStats(r.data))
      .catch(() => showAlert("error", "Could not load stats."))
      .finally(() => setLoadingStats(false));
  }, [selectedUser.user_email, showAlert]);

  const loadHistory = useCallback((pg = 1, conn = "", from = "", to = "") => {
    setLoadingHist(true);
    getQueryHistory(selectedUser.user_email, pg, LIMIT, conn || undefined, from || undefined, to || undefined)
      .then((r) => { setHistory(r.data.items || []); setTotal(r.data.total || 0); setPage(pg); })
      .catch(() => showAlert("error", "Could not load query history."))
      .finally(() => setLoadingHist(false));
  }, [selectedUser.user_email, showAlert]);

  useEffect(() => { loadStats(); loadHistory(1, "", "", ""); }, [loadStats, loadHistory]);

  const handleConnFilter = (conn) => { setConnFilter(conn); loadHistory(1, conn, dateFrom, dateTo); };
  const handleDateFilter = ()     => loadHistory(1, connFilter, dateFrom, dateTo);
  const handleClearDates = ()     => { setDateFrom(""); setDateTo(""); loadHistory(1, connFilter, "", ""); };
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <div className="admin-panel" style={{ marginBottom: 16 }}>
        <div className="panel-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h2>
              <span className="qh-person-avatar" style={{ display: "inline-flex", marginRight: 10 }}>
                {(selectedUser.user_name || selectedUser.user_email)[0].toUpperCase()}
              </span>
              {selectedUser.user_name}
            </h2>
            <p className="panel-sub">{selectedUser.user_email} · {ROLE_LABELS[selectedUser.user_role]?.label || selectedUser.user_role}</p>
          </div>
          <button className="btn btn-sm" onClick={onBack}>← Back</button>
        </div>
        <div className="qh-stats-row">
          <div className="qh-stat-card">
            <div className="qh-stat-value">{loadingStats ? "…" : (stats?.total_queries ?? 0)}</div>
            <div className="qh-stat-label">Total Queries</div>
          </div>
          <div className="qh-stat-card qh-stat-card--today">
            <div className="qh-stat-value">{loadingStats ? "…" : (stats?.today_queries ?? 0)}</div>
            <div className="qh-stat-label">Today</div>
          </div>
        </div>
      </div>

      <div className="admin-panel">
        <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h2>Full Query History</h2>
            <p className="panel-sub">{total} {total === 1 ? "query" : "queries"} total</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <ConnectorSelect value={connFilter} onChange={handleConnFilter} />
            <input type="date" className="admin-inline-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" style={{ fontSize: 13, padding: "5px 10px" }} />
            <input type="date" className="admin-inline-input" value={dateTo}   onChange={e => setDateTo(e.target.value)}   title="To date"   style={{ fontSize: 13, padding: "5px 10px" }} />
            <button className="btn btn-sm btn-primary" onClick={handleDateFilter} style={{ fontSize: 12 }}>Filter</button>
            {(dateFrom || dateTo) && <button className="btn btn-sm" onClick={handleClearDates} style={{ fontSize: 12 }}>✕ Clear</button>}
            <button className="btn btn-sm" onClick={() => loadHistory(page, connFilter, dateFrom, dateTo)} title="Refresh">🔄</button>
          </div>
        </div>
        {loadingHist ? (
          <div className="panel-loading">Loading…</div>
        ) : history.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 14, padding: "24px 0", textAlign: "center" }}>
            {connFilter || dateFrom || dateTo ? "No queries match the current filters." : `${selectedUser.user_name} hasn't made any queries yet.`}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {history.map((item) => {
                const isOpen = expanded === item.id;
                return (
                  <div key={item.id} className="qh-history-card">
                    <div className="qh-history-row" onClick={() => setExpanded(isOpen ? null : item.id)}>
                      <span className="qh-connector-badge">{connectorLabel(item.connector)}</span>
                      <div className="qh-history-query">
                        {item.query_text.length > 120 ? item.query_text.slice(0, 120) + "…" : item.query_text}
                      </div>
                      <span className="qh-history-date">{fmt.datetime(item.created_at)}</span>
                      <button
                        className="qh-delete-btn"
                        title="Delete this entry"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm("Delete this query entry? This cannot be undone.")) return;
                          try {
                            await deleteQueryHistoryEntry(item.id);
                            setHistory(prev => prev.filter(h => h.id !== item.id));
                            setTotal(prev => prev - 1);
                            if (expanded === item.id) setExpanded(null);
                          } catch { showAlert("error", "Could not delete entry."); }
                        }}
                      >🗑️</button>
                      <span style={{ color: "#9ca3af", fontSize: 13, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>
                    </div>
                    {isOpen && (
                      <div className="qh-history-detail">
                        <div className="qh-detail-section">
                          <div className="qh-detail-label">Query</div>
                          <div className="qh-detail-text">{item.query_text}</div>
                        </div>
                        <div className="qh-detail-section">
                          <div className="qh-detail-label">Answer</div>
                          <div className="qh-detail-text"><AnswerContent content={item.answer_text} /></div>
                        </div>
                        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                          <span>Answered by: <strong>{connectorLabel(item.connector)}</strong></span>
                          <span>User selected: <strong>{selectedSourcesLabel(item.selected_sources)}</strong></span>
                          <span>Date: <strong>{fmt.datetime(item.created_at)}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="qh-pagination">
                <button className="btn btn-sm" disabled={page <= 1} onClick={() => loadHistory(page - 1, connFilter, dateFrom, dateTo)}>← Prev</button>
                <span style={{ fontSize: 13, color: "#6b7280" }}>Page {page} of {totalPages}</span>
                <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => loadHistory(page + 1, connFilter, dateFrom, dateTo)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Today's Queries (10PM–10PM live feed)
// ═══════════════════════════════════════════════════════════════════════════════

function TodayQueriesPanel({ showAlert }) {
  const [items,      setItems]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [window_,    setWindow]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [connFilter, setConnFilter] = useState("");
  const [search,     setSearch]     = useState("");
  const LIMIT = 50;

  const load = useCallback((pg = 1, role = "", conn = "", q = "") => {
    setLoading(true);
    getTodayQueries(pg, LIMIT, role || undefined, conn || undefined, q || undefined)
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setPage(pg);
        setWindow({ start: r.data.window_start, end: r.data.window_end });
      })
      .catch(() => showAlert("error", "Could not load today's queries."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(1, "", "", ""); }, [load]);

  const applyFilters = () => load(1, roleFilter, connFilter, search);
  const clearFilters = () => { setRoleFilter(""); setConnFilter(""); setSearch(""); load(1, "", "", ""); };
  const totalPages = Math.ceil(total / LIMIT);

  const windowLabel = window_ ? (() => {
    const s = new Date(window_.start);
    const e = new Date(window_.end);
    const d = (d) => d.toLocaleDateString([], { month: "short", day: "numeric" });
    return `${d(s)} 10 PM → ${d(e)} 10 PM`;
  })() : "";

  return (
    <div className="admin-panel">
      <div className="panel-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2>Today's Queries</h2>
          <p className="panel-sub">
            {windowLabel ? `Window: ${windowLabel}` : "Loading…"}{" · "}{total} {total === 1 ? "query" : "queries"}
          </p>
        </div>
        <button className="btn btn-sm" onClick={() => load(page, roleFilter, connFilter, search)} title="Refresh">🔄 Refresh</button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="text"
          className="admin-inline-input"
          placeholder="Search name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && applyFilters()}
          style={{ fontSize: 13, padding: "5px 10px", minWidth: 180 }}
        />
        <select className="admin-inline-input" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ fontSize: 13, padding: "5px 10px" }}>
          <option value="">All roles</option>
          <option value="admin">🔑 Admin</option>
          <option value="project_manager">📋 Project Manager</option>
          <option value="employee">👤 Employee</option>
        </select>
        <ConnectorSelect value={connFilter} onChange={setConnFilter} />
        <button className="btn btn-sm btn-primary" onClick={applyFilters} style={{ fontSize: 12 }}>Filter</button>
        {(roleFilter || connFilter || search) && (
          <button className="btn btn-sm" onClick={clearFilters} style={{ fontSize: 12 }}>✕ Clear</button>
        )}
      </div>

      {loading ? (
        <div className="panel-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 14, padding: "32px 0", textAlign: "center" }}>
          {roleFilter || connFilter || search ? "No queries match the current filters." : "No queries have been made in this window yet."}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((item) => {
              const isOpen = expanded === item.id;
              const meta   = ROLE_LABELS[item.user_role] || { emoji: "👤", label: item.user_role };
              return (
                <div key={item.id} className="qh-history-card">
                  <div className="qh-history-row" onClick={() => setExpanded(isOpen ? null : item.id)}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 170, flexShrink: 0 }}>
                      <span className="qh-person-avatar" style={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}>
                        {(item.user_name || item.user_email)[0].toUpperCase()}
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.user_name}</span>
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>{meta.emoji} {meta.label}</span>
                      </span>
                    </span>
                    <span className="qh-connector-badge" style={{ flexShrink: 0 }}>{connectorLabel(item.connector)}</span>
                    <div className="qh-history-query" style={{ flex: 1 }}>
                      {item.query_text.length > 100 ? item.query_text.slice(0, 100) + "…" : item.query_text}
                    </div>
                    <span className="qh-history-date" style={{ flexShrink: 0 }}>{fmt.time(item.created_at)}</span>
                    <span style={{ color: "#9ca3af", fontSize: 13, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                  {isOpen && (
                    <div className="qh-history-detail">
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                        <span style={{ fontSize: 12, color: "#6b7280" }}><strong>{item.user_name}</strong> · {item.user_email}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{fmt.datetime(item.created_at)}</span>
                      </div>
                      <div className="qh-detail-section">
                        <div className="qh-detail-label">Query</div>
                        <div className="qh-detail-text">{item.query_text}</div>
                      </div>
                      <div className="qh-detail-section">
                        <div className="qh-detail-label">Answer</div>
                        <div className="qh-detail-text"><AnswerContent content={item.answer_text} /></div>
                      </div>
                      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                        <span>Connector: <strong>{connectorLabel(item.connector)}</strong></span>
                        <span>Sources: <strong>{selectedSourcesLabel(item.selected_sources)}</strong></span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="qh-pagination">
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => load(page - 1, roleFilter, connFilter, search)}>← Prev</button>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Page {page} of {totalPages}</span>
              <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => load(page + 1, roleFilter, connFilter, search)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — Daily Report Settings
// ═══════════════════════════════════════════════════════════════════════════════

function DailyReportPanel({ showAlert }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [toggling, setToggling] = useState({});
  const [sending,  setSending]  = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getReportRecipients()
      .then(r => setData(r.data))
      .catch(() => showAlert("error", "Could not load report settings."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(); }, [load]);

  const toggleAdmin = async (email, enabled) => {
    setToggling(t => ({ ...t, [email]: true }));
    try {
      await setAdminReportNotification(email, enabled);
      setData(d => ({ ...d, admins: d.admins.map(a => a.email === email ? { ...a, query_report_enabled: enabled } : a) }));
    } catch (err) {
      showAlert("error", err?.response?.data?.detail || "Could not update setting.");
    } finally {
      setToggling(t => ({ ...t, [email]: false }));
    }
  };

  const togglePm = async (email, enabled) => {
    setToggling(t => ({ ...t, [email]: true }));
    try {
      await setPmReportNotification(email, enabled);
      setData(d => ({ ...d, project_managers: d.project_managers.map(pm => pm.email === email ? { ...pm, query_report_enabled: enabled } : pm) }));
    } catch {
      showAlert("error", "Could not update setting.");
    } finally {
      setToggling(t => ({ ...t, [email]: false }));
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm("Send the daily query report now to all enabled recipients?")) return;
    setSending(true);
    try {
      const r = await sendReportNow();
      showAlert(r.data.sent ? "success" : "error",
        r.data.sent ? "Report sent successfully." : "Not sent — no enabled recipients or SMTP not configured.");
    } catch {
      showAlert("error", "Failed to send report.");
    } finally {
      setSending(false);
    }
  };

  const nextSend = (() => {
    const now = new Date(), next = new Date(now);
    next.setHours(22, 0, 0, 0);
    if (now >= next) next.setDate(next.getDate() + 1);
    return next.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  })();

  if (loading) return <div className="admin-panel"><div className="panel-loading">Loading…</div></div>;

  const admins = data?.admins || [];
  const pms    = data?.project_managers || [];
  const totalEnabled = [...admins, ...pms].filter(r => r.query_report_enabled).length;

  function RecipientRow({ person, onToggle, isAdmin }) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
        <span className="qh-person-avatar" style={{ width: 32, height: 32, fontSize: 13, flexShrink: 0 }}>
          {(person.name || person.email)[0].toUpperCase()}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{person.name}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{person.email}</div>
        </span>
        <span className="qh-role-pill" style={{ flexShrink: 0 }}>
          {isAdmin ? "🔑 Admin" : "📋 Project Manager"}
          {isAdmin && person.source === "env" && <span style={{ marginLeft: 4, fontSize: 10, color: "#9ca3af" }}>(env)</span>}
        </span>
        <span style={{ flexShrink: 0, fontSize: 12, color: person.query_report_enabled ? "#16a34a" : "#9ca3af", minWidth: 28, textAlign: "right" }}>
          {person.query_report_enabled ? "On" : "Off"}
        </span>
        <Toggle
          checked={person.query_report_enabled}
          onChange={val => onToggle(person.email, val)}
          disabled={toggling[person.email] || (isAdmin && person.source === "env")}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-panel" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ marginBottom: 6 }}>Daily Query Report</h2>
            <p className="panel-sub" style={{ margin: 0 }}>
              Sent every day at <strong>10 PM</strong>. Covers queries from <strong>10 PM yesterday → 10 PM today</strong>.
              Next send: <strong>{nextSend}</strong>.
            </p>
          </div>
          <button className="btn btn-sm btn-primary" onClick={handleSendNow} disabled={sending} style={{ whiteSpace: "nowrap" }}>
            {sending ? "Sending…" : "📧 Send Now"}
          </button>
        </div>
        <div className="qh-stats-row" style={{ marginTop: 16 }}>
          <div className="qh-stat-card">
            <div className="qh-stat-value">{admins.length + pms.length}</div>
            <div className="qh-stat-label">Total Recipients</div>
          </div>
          <div className="qh-stat-card qh-stat-card--today">
            <div className="qh-stat-value">{totalEnabled}</div>
            <div className="qh-stat-label">Enabled</div>
          </div>
        </div>
      </div>

      <div className="admin-panel" style={{ marginBottom: 16 }}>
        <div className="panel-header">
          <h2>Knowledge Admins</h2>
          <p className="panel-sub">Env-listed admins always receive the report (toggle disabled).</p>
        </div>
        {admins.length === 0
          ? <div style={{ color: "#9ca3af", fontSize: 14, padding: "12px 0" }}>No admins configured.</div>
          : admins.map(a => <RecipientRow key={a.email} person={a} onToggle={toggleAdmin} isAdmin={true} />)
        }
      </div>

      <div className="admin-panel">
        <div className="panel-header">
          <h2>Project Managers</h2>
          <p className="panel-sub">Toggle to include or exclude each PM from the daily report.</p>
        </div>
        {pms.length === 0
          ? <div style={{ color: "#9ca3af", fontSize: 14, padding: "12px 0" }}>No project managers added yet.</div>
          : pms.map(pm => <RecipientRow key={pm.email} person={pm} onToggle={togglePm} isAdmin={false} />)
        }
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════

const MAIN_TABS = [
  { key: "members", label: "👥 Members" },
  { key: "today",   label: "📋 Today's Queries" },
  { key: "report",  label: "📧 Daily Report" },
];

export default function QueryHistory() {
  const { user }                    = useAuth();
  const { darkTheme, setDarkTheme } = useChat();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertType,   setAlertType]   = useState("");
  const [alertMsg,    setAlertMsg]    = useState("");
  const [activeTab,   setActiveTab]   = useState("members");
  const [users,        setUsers]      = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const showAlert = useCallback((type, msg) => {
    setAlertType(type); setAlertMsg(msg);
    setTimeout(() => setAlertMsg(""), 5000);
  }, []);

  useEffect(() => {
    setLoadingUsers(true);
    getQueryHistoryUsers()
      .then((r) => setUsers(r.data.users || {}))
      .catch(() => showAlert("error", "Could not load user list."))
      .finally(() => setLoadingUsers(false));
  }, [showAlert]);

  const handleTabChange = (tab) => { setActiveTab(tab); if (tab !== "members") setSelectedUser(null); };

  return (
    <div className={"chat-page" + (darkTheme ? " dark-theme" : "")}>
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <div className={"gpt-sidebar" + (sidebarOpen ? " gpt-sidebar-open" : "")}>
        <div className="gpt-sidebar-brand">
          <img src={guruLogo} alt="Guru" className="gpt-brand-logo" />
          <div>
            <div className="gpt-brand-name">Guru</div>
            <div className="gpt-brand-sub">SEO Knowledge Agent</div>
          </div>
        </div>
        <div className="gpt-sidebar-topnav">
          <NavLink to="/chat"          className="gpt-nav-link"><span>💬</span> Back to Chat</NavLink>
          <NavLink to="/admin"         className="gpt-nav-link"><span>⚙️</span> Admin</NavLink>
          <NavLink to="/projects"      className="gpt-nav-link"><span>📋</span> Projects</NavLink>
          <NavLink to="/notifications" className="gpt-nav-link"><span>📣</span> Notifications</NavLink>
          <NavLink to="/query-history" className="gpt-nav-link active-nav"><span>🔍</span> Query History</NavLink>
        </div>
        <div className="gpt-sidebar-spacer" />
        <div style={{ padding: "8px 16px", flexShrink: 0 }}>
          <button
            className="theme-toggle-btn"
            style={{ width: "100%", justifyContent: "flex-start", gap: "8px" }}
            onClick={() => setDarkTheme((d) => !d)}
          >
            {darkTheme ? "☀️ Light mode" : "🌙 Dark mode"}
          </button>
        </div>
        <AccountMenu />
      </div>

      <div className="query-history-content">
        <div className="mobile-page-header">
          <button type="button" className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>Query History</h1>
        </div>
        <p className="subtitle">See who queried what, when, and which connector answered.</p>

        <Alert type={alertType} msg={alertMsg} onClose={() => setAlertMsg("")} />

        {/* Top-level tabs */}
        <div className="admin-tabs" style={{ marginBottom: 20 }}>
          {MAIN_TABS.map(t => (
            <button
              key={t.key}
              className={"admin-tab" + (activeTab === t.key ? " active" : "")}
              onClick={() => handleTabChange(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "members" && (
          loadingUsers
            ? <div className="admin-panel"><div className="panel-loading">Loading…</div></div>
            : !selectedUser
              ? <PeopleList users={users} onSelectUser={setSelectedUser} />
              : <UserHistoryView user={selectedUser} onBack={() => setSelectedUser(null)} showAlert={showAlert} />
        )}

        {activeTab === "today"  && <TodayQueriesPanel showAlert={showAlert} />}
        {activeTab === "report" && <DailyReportPanel  showAlert={showAlert} />}
      </div>
    </div>
  );
}
